// MultiQrPdf.tsx
import React, { useState, useEffect, useCallback } from "react";
import jsPDF from "jspdf";
import { toDataURL } from "qrcode";
import { Product } from "../utils/types";
import { fetchProducts4manual } from "../utils/api";
import Pagination from "./Pagination";

const PRODUCTS_PER_PAGE_MODAL = 18;

const MultiQrPdf: React.FC = () => {

    const [items, setItems] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [errorItems, setErrorItems] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(PRODUCTS_PER_PAGE_MODAL);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterMode, setFilterMode] = useState<'all' | 'inStockOnly'>('all');

    const [selected, setSelected] = useState<Product[]>([]);

    // 🔹 select all toggle
    const selectAll = () => {
        // Add all items from the current page to the selection, avoiding duplicates.
        setSelected(prevSelected => {
            const selectedUuids = new Set(prevSelected.map(p => p.product_uuid));
            const newItemsToAdd = items.filter(item => !selectedUuids.has(item.product_uuid));
            return [...prevSelected, ...newItemsToAdd];
        });
    };
    const clearAll = () => {
        setSelected([]);
    };

    const [pageSize, setPageSize] = useState<"a4" | "a5">("a4");
    const [textstackPadding, setTextstackPadding] = useState<number>(4);
    const [textsidePadding, setTextsidePadding] = useState<number>(6);
    const [qrCodeSize, setQRCodeSize] = useState<number>(0.6);
    const [textLayout, setTextLayout] = useState<'stack' | 'side'>('stack');
    const [fillDirection, setFillDirection] = useState<'row-first' | 'col-first'>('row-first');
    const [qrLabelLayout, setQrLabelLayout] = useState<'stack' | 'side'>('stack');

    const toggleSelect = (item: Product) => {
        setSelected((prev) =>
            prev.some(p => p.product_uuid === item.product_uuid)
                ? prev.filter((p) => p.product_uuid !== item.product_uuid)
                : [...prev, item]
        );
    };

    // 🔹 fetch Items from backend API

    const fetchItems = useCallback(async () => {
        try {
            const { products: fetchedItems, total_pages } = await fetchProducts4manual(
                currentPage,
                searchTerm,
                null,
                null,
                itemsPerPage,
                'updated_at DESC',
                filterMode === 'inStockOnly' ? true : undefined
            );
            setItems(fetchedItems);
            setTotalPages(total_pages);
        } catch (err: any) {
            setErrorItems(err.message || 'Failed to load stocks.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, filterMode, itemsPerPage]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);


    if (errorItems) return <div className="text-red-600">{errorItems}</div>;

    const generatePdf = async () => {
        const doc = new jsPDF({ format: pageSize, unit: "mm" });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // --- Grid layout ---
        // A4 -> 3 columns x 8 rows
        // A5 -> 2 columns x 4 rows
        const cols = pageSize === "a4" ? 3 : 2;
        const rows = pageSize === "a4" ? 8 : 4;
        const itemsPerPage = cols * rows;

        const cellWidth = pageWidth / cols;
        const cellHeight = pageHeight / rows;

        const qrSize = Math.min(cellWidth, cellHeight) * qrCodeSize; //0.3; // QR code size

        // Filter for selected items first to avoid repeated finds
        const selectedItems = selected;

        // Generate all QR codes in parallel for better performance
        const qrCodePromises = selectedItems.map(item =>
            toDataURL(JSON.stringify({
                action: '',
                id: item.product_uuid,
                type: 'item', // Assuming MultiQrPdf is only for items
                ts: new Date().toISOString(),
            }), {
                width: qrSize,
                margin: 1,
                errorCorrectionLevel: 'H',
            }).catch(err => {
                console.error(`Failed to generate QR for ${item.product_uuid}`, err);
                return null; // Return null on error to avoid breaking Promise.all
            })
        );

        const qrDataUrls = await Promise.all(qrCodePromises);

        // Add items to PDF
        selectedItems.forEach((item, index) => {
            const qrDataUrl = qrDataUrls[index];
            if (!qrDataUrl) {
                return; // Skip if QR code generation failed
            }

            // Handle pagination
            if (index > 0 && index % itemsPerPage === 0) {
                doc.addPage();
            }

            const itemOnPageIndex = index % itemsPerPage;
            let col: number;
            let row: number;

            if (fillDirection === 'row-first') {
                // Fills page left-to-right, then top-to-bottom (like flex-row with wrap)
                col = itemOnPageIndex % cols;
                row = Math.floor(itemOnPageIndex / cols);
            } else { // 'col-first'
                // Fills page top-to-bottom, then left-to-right (like flex-col with wrap)
                col = Math.floor(itemOnPageIndex / rows);
                row = itemOnPageIndex % rows;
            }

            if (qrLabelLayout === 'stack') {
                // "flex-row" (user term): QR code on top, text label on bottom
                const x = col * cellWidth + (cellWidth - qrSize) / 2;
                const y = row * cellHeight + (cellHeight - qrSize) / 2 - 5;

                doc.addImage(qrDataUrl, "PNG", x, y, qrSize, qrSize);

                // Add label text (below QR)
                doc.setFontSize(8);
                const textContent = textLayout === 'stack'
                    ? [item.product_code || "", item.product_name || ""]
                    : `${item.product_code || ""} - ${item.product_name || ""}`;
                doc.text(textContent, x + qrSize / 2, y + qrSize + textstackPadding, {
                    align: "center",
                    maxWidth: cellWidth - textsidePadding,
                });
            } else { // 'side'
                // "flex-col" (user term): QR code on left, text label on right
                const qrTextGap = 2; // gap in mm

                // QR code position (left side of cell, stackly centered)
                const qrX = col * cellWidth + textsidePadding;
                const qrY = row * cellHeight + (cellHeight - qrSize) / 2;
                doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

                // Text label position (right of QR code, stackly centered)
                const textX = qrX + qrSize + qrTextGap;
                const textY = row * cellHeight + cellHeight / 4;
                const textMaxWidth = cellWidth - (qrX - col * cellWidth) - qrSize - qrTextGap - textsidePadding;

                doc.setFontSize(8);
                const textContent = textLayout === 'stack'
                    ? [item.product_code || "", item.product_name || ""]
                    : `${item.product_code || ""} - ${item.product_name || ""}`;

                doc.text(textContent, textX, textY, {
                    align: "left",
                    baseline: "middle",
                    maxWidth: textMaxWidth,
                });
            }
        });

        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
        const day = today.getDate().toString().padStart(2, '0');
        const minutes = today.getMinutes().toString().padStart(2, '0');
        const seconds = today.getSeconds().toString().padStart(2, '0');
        const yymmdd = `${year}${month}${day}`;
        doc.save(`qr-labels-${selected.length}-${yymmdd}-${minutes}${seconds}.pdf`);
    };

    return (
        <div className="p-1 flex flex-col md:flex-row">

            <div className="p-4 w-full md:w-3/4">
                <h2 className="text-lg font-bold mb-2">Select Items to Print QR Labels</h2>

                {/* config box */}

                <div className="flex flex-col md:flex-row bg-gray-200 rounded-xl p-1">



                    <div className="mb-3 px-2">
                        <label className="mr-2">Page Size:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(e.target.value as "a4" | "a5")}
                            className="border p-1 rounded"
                        >
                            <option value="a4">A4 (3 × 8 = 24 labels)</option>
                            <option value="a5">A5 (2 × 4 = 8 labels)</option>
                        </select>
                    </div>

                    <div className="mb-3 px-2">
                        <label className="mr-2">QR & Label Layout:</label>
                        <select
                            value={qrLabelLayout}
                            onChange={(e) => setQrLabelLayout(e.target.value as 'stack' | 'side')}
                            className="border p-1 rounded"
                        >
                            <option value="stack">QR on Top, Text Below (flex-col)</option>
                            <option value="side">QR Left, Text Right (flex-row)</option>
                        </select>
                    </div>


                    {/* <div className="mb-3 px-2">
                        <label className="mr-2">Fill Direction:</label>
                        <select
                            value={fillDirection}
                            onChange={(e) => setFillDirection(e.target.value as 'row-first' | 'col-first')}
                            className="border p-1 rounded"
                        >
                            <option value="row-first">Row First (flex-row)</option>
                            <option value="col-first">Column First (flex-col)</option>
                        </select>
                    </div> */}

                    {/* <div className="mb-3 px-2">
                        <label className="mr-2">QR/Label Layout:</label>
                        <select
                            value={qrLabelLayout}
                            onChange={(e) => setQrLabelLayout(e.target.value as 'stack' | 'side')}
                            className="border p-1 rounded"
                        >
                            <option value="stack">Top-Bottom (flex-row)</option>
                            <option value="side">Left-Right (flex-col)</option>
                        </select>
                    </div> */}

                    <div className="mb-1 px-2 text-xs">
                        <label className="mr-2">Text stack Padding (from QR):</label>
                        <input
                            type="number"
                            value={textstackPadding}
                            onChange={(e) => setTextstackPadding(Number(e.target.value))}
                            className="border p-1 rounded w-20"
                            step="0.5"
                            min="0"
                        />
                    </div>
                    <div className="mb-1 px-2 text-xs">
                        <label className="mr-2">Text side Padding (from edges):</label>
                        <input
                            type="number"
                            value={textsidePadding}
                            onChange={(e) => setTextsidePadding(Number(e.target.value))}
                            className="border p-1 rounded w-20"
                            step="0.5"
                            min="0"
                        />
                    </div>
                    <div className="mb-1 px-2 text-xs">
                        <label className="mr-2">QR Code Size:</label>
                        <input
                            type="number"
                            value={qrCodeSize}
                            onChange={(e) => setQRCodeSize(Number(e.target.value))}
                            className="border p-1 rounded w-20"
                            step="0.1"
                            min="0.3"
                            max="0.6"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-2 gap-1 m-1">

                    {/* search box */}
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="border p-1 rounded w-full mb-3 border border-gray-600"
                    />

                    {/* select all / clear */}
                    <div className="mb-1 space-x-2">
                        <button
                            onClick={selectAll}
                            className="text-xs md:text-sm bg-green-500 text-white px-2 py-1 rounded"
                        >
                            Select All (Filtered)
                        </button>
                        <button
                            onClick={clearAll}
                            className="text-xs md:text-sm bg-red-500 text-white px-3 py-1 rounded"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
                <p className="mb-2 text-sm text-gray-600">Selected {selected.length} items.</p>

                {totalPages > 1 && (
                    <div className="flex flex-col md:flex-row justify-between my-2 py-2 border-t">

                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

                        <div className="mb-3 px-2">
                            <label className="mr-2">Items Per Page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                className="border p-1 rounded"
                            >
                                <option value="18">18</option>
                                <option value="36">36</option>
                                <option value="54">54</option>
                                <option value="90">90</option>
                                <option value="180">180</option>
                            </select>
                        </div>

                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-sm">
                    {items.map((item: Product) => (
                        <label
                            key={item.product_uuid}
                            className="flex items-center space-x-2 border p-2 rounded cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                checked={selected.some(p => p.product_uuid === item.product_uuid)}
                                onChange={() => toggleSelect(item)}
                            />
                            <span>{item.product_code}</span>
                            <span>{item.product_name}</span>
                        </label>
                    ))}
                </div>

                {totalPages > 1 && (
                    <div className="flex flex-col md:flex-row justify-between my-2 py-2 border-t">

                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

                        <div className="mb-3 px-2">
                            <label className="mr-2">Items Per Page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                className="border p-1 rounded"
                            >
                                <option value="18">18</option>
                                <option value="36">36</option>
                                <option value="54">54</option>
                                <option value="90">90</option>
                                <option value="180">180</option>
                            </select>
                        </div>

                    </div>
                )}

                <button
                    onClick={generatePdf}
                    disabled={selected.length === 0}
                    className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
                >
                    Generate PDF Labels
                </button>
            </div>

            <div className="p-4 bg-emerald-100 w-full md:w-1/4">

                <button
                    onClick={generatePdf}
                    disabled={selected.length === 0}
                    className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400 w-full mb-4"
                >
                    Generate PDF Labels
                </button>

                Selected : {selected.length} items.
                <ol className="mt-4 text-xs">
                    {selected.map(item => (
                        <li key={item.product_uuid} className="py-1">{item.product_code} - {item.product_name}</li>
                    ))}
                </ol>
            </div>

        </div>
    );
};

export default MultiQrPdf;
